import React, { useId } from 'react';
import { FiBarChart2, FiDownload, FiRefreshCw } from 'react-icons/fi';
import { ButtonSpinner } from '../LoadingButton';
import { downloadCsv } from '../../utils/exportCsv';
import {
  EmptyState as ErpEmpty,
  ErrorState as ErpError,
  LoadingSkeleton as ErpSkeleton,
  errorMessage,
  formatCents as money,
  statusLabel,
} from '../shared/Ui';

const REPORT_TITLES = {
  'trial-balance': 'Trial balance',
  'profit-loss': 'Profit & loss',
  'balance-sheet': 'Balance sheet',
  'ar-aging': 'AR aging',
  'ap-aging': 'AP aging',
};

const EMPTY_HINTS = {
  'trial-balance': 'Post a journal entry in this period to see account balances.',
  'profit-loss': 'Income and expense accounts appear here once entries are posted.',
  'balance-sheet': 'Asset, liability and equity balances appear once entries are posted.',
  'ar-aging': 'Open customer invoices appear here with their ageing bucket.',
  'ap-aging': 'Unpaid vendor bills appear here with their ageing bucket.',
};

const BUCKET_LABELS = {
  current: 'Current',
  days_1_30: '1–30 days',
  days_31_60: '31–60 days',
  days_61_90: '61–90 days',
  days_90_plus: '90+ days',
};

const BUCKET_TONES = {
  days_31_60: 'overdue',
  days_61_90: 'overdue',
  days_90_plus: 'critical',
};

const text = (key, label) => ({ key, label });
const amount = (key, label) => ({
  key,
  label,
  numeric: true,
  render: (row) => money(row[key], row.currency),
  format: (row) => ((Number(row[key]) || 0) / 100).toFixed(2),
});
const count = (key, label) => ({ key, label, numeric: true });

function sumBy(rows, key) {
  return (rows || []).reduce((total, row) => total + (Number(row[key]) || 0), 0);
}

/**
 * Normalise each report payload into columns + labelled sections so the table
 * reads like a financial statement instead of a raw key dump.
 */
// eslint-disable-next-line no-unused-vars
function buildReportLegacy(data, report) {
  if (!data) return null;

  if (report === 'trial-balance') {
    const rows = (Array.isArray(data) ? data : []).map((r) => ({
      code: r.code,
      name: r.name,
      account_type: r.account_type,
      debit_cents: r.debit_cents || 0,
      credit_cents: r.credit_cents || 0,
    }));
    return {
      columns: [
        text('code', 'Code'),
        text('name', 'Account'),
        { ...text('account_type', 'Type'), render: (row) => statusLabel(row.account_type) },
        amount('debit_cents', 'Debit'),
        amount('credit_cents', 'Credit'),
      ],
      sections: [{ rows }],
      footer: {
        label: 'Totals',
        values: {
          debit_cents: money(sumBy(rows, 'debit_cents')),
          credit_cents: money(sumBy(rows, 'credit_cents')),
        },
      },
    };
  }

  if (report === 'profit-loss') {
    const columns = [text('code', 'Code'), text('name', 'Account'), amount('amount_cents', 'Amount')];
    const income = (data.income || []).map((r) => ({ code: r.code, name: r.name, amount_cents: r.amount_cents || 0 }));
    const expense = (data.expense || []).map((r) => ({ code: r.code, name: r.name, amount_cents: r.amount_cents || 0 }));
    return {
      columns,
      sections: [
        {
          label: 'Income',
          rows: income,
          subtotal: { label: 'Total income', values: { amount_cents: money(data.income_total_cents) } },
        },
        {
          label: 'Expense',
          rows: expense,
          subtotal: { label: 'Total expense', values: { amount_cents: money(data.expense_total_cents) } },
        },
      ],
      footer: { label: 'Net income', values: { amount_cents: money(data.net_income_cents) } },
    };
  }

  if (report === 'balance-sheet') {
    const columns = [text('code', 'Code'), text('name', 'Account'), amount('balance_cents', 'Balance')];
    const labels = { asset: 'Assets', liability: 'Liabilities', equity: 'Equity' };
    const sections = ['asset', 'liability', 'equity'].map((key) => {
      const rows = (data[key] || []).map((r) => ({ code: r.code, name: r.name, balance_cents: r.balance_cents || 0 }));
      return {
        label: labels[key],
        rows,
        subtotal: {
          label: `Total ${labels[key].toLowerCase()}`,
          values: { balance_cents: money(data.totals_cents?.[key] ?? sumBy(rows, 'balance_cents')) },
        },
      };
    });
    const liabilitiesAndEquity =
      (Number(data.totals_cents?.liability) || 0) + (Number(data.totals_cents?.equity) || 0);
    return {
      columns,
      sections,
      footer: { label: 'Liabilities + equity', values: { balance_cents: money(liabilitiesAndEquity) } },
    };
  }

  if (report === 'ar-aging' || report === 'ap-aging') {
    const isAr = report === 'ar-aging';
    const source = isAr ? data.invoices : data.bills;
    const amountKey = isAr ? 'total_cents' : 'open_cents';
    const rows = (source || []).map((r) => ({
      number: isAr ? r.number : r.bill_number,
      party: isAr ? r.client_name : r.vendor_name,
      [amountKey]: r[amountKey] || 0,
      days_outstanding: r.days_outstanding,
      bucket: BUCKET_LABELS[r.bucket] || r.bucket,
      currency: r.currency,
    }));
    return {
      columns: [
        text('number', isAr ? 'Invoice' : 'Bill'),
        text('party', isAr ? 'Customer' : 'Vendor'),
        amount(amountKey, isAr ? 'Total' : 'Open'),
        count('days_outstanding', 'Days'),
        text('bucket', 'Bucket'),
      ],
      sections: [{ rows }],
      footer: { label: 'Total outstanding', values: { [amountKey]: money(sumBy(rows, amountKey)) } },
      buckets: Object.entries(data.buckets_cents || {}).map(([key, cents]) => ({
        key,
        label: BUCKET_LABELS[key] || key,
        cents,
        tone: BUCKET_TONES[key],
      })),
      asOf: data.as_of,
    };
  }

  return null;
}

function buildReport(data, report, currency) {
  if (!data) return null;

  if (report === 'trial-balance') {
    const rows = (Array.isArray(data) ? data : []).map((r) => ({
      code: r.code,
      name: r.name,
      account_type: r.account_type,
      debit_cents: r.debit_cents || 0,
      credit_cents: r.credit_cents || 0,
      currency,
    }));
    return {
      columns: [
        text('code', 'Code'),
        text('name', 'Account'),
        { ...text('account_type', 'Type'), render: (row) => statusLabel(row.account_type) },
        amount('debit_cents', 'Debit'),
        amount('credit_cents', 'Credit'),
      ],
      sections: [{ rows }],
      footer: {
        label: 'Totals',
        values: {
          debit_cents: money(sumBy(rows, 'debit_cents'), currency),
          credit_cents: money(sumBy(rows, 'credit_cents'), currency),
        },
      },
    };
  }

  if (report === 'profit-loss') {
    const columns = [text('code', 'Code'), text('name', 'Account'), amount('amount_cents', 'Amount')];
    const income = (data.income || []).map((r) => ({
      code: r.code,
      name: r.name,
      amount_cents: r.amount_cents || 0,
      currency,
    }));
    const expense = (data.expense || []).map((r) => ({
      code: r.code,
      name: r.name,
      amount_cents: r.amount_cents || 0,
      currency,
    }));
    return {
      columns,
      sections: [
        {
          label: 'Income',
          rows: income,
          subtotal: {
            label: 'Total income',
            values: { amount_cents: money(data.income_total_cents, currency) },
          },
        },
        {
          label: 'Expense',
          rows: expense,
          subtotal: {
            label: 'Total expense',
            values: { amount_cents: money(data.expense_total_cents, currency) },
          },
        },
      ],
      footer: { label: 'Net income', values: { amount_cents: money(data.net_income_cents, currency) } },
    };
  }

  if (report === 'balance-sheet') {
    const columns = [text('code', 'Code'), text('name', 'Account'), amount('balance_cents', 'Balance')];
    const labels = { asset: 'Assets', liability: 'Liabilities', equity: 'Equity' };
    const sections = ['asset', 'liability', 'equity'].map((key) => {
      const rows = (data[key] || []).map((r) => ({
        code: r.code,
        name: r.name,
        balance_cents: r.balance_cents || 0,
        currency,
      }));
      return {
        label: labels[key],
        rows,
        subtotal: {
          label: `Total ${labels[key].toLowerCase()}`,
          values: { balance_cents: money(data.totals_cents?.[key] ?? sumBy(rows, 'balance_cents'), currency) },
        },
      };
    });
    const liabilitiesAndEquity =
      (Number(data.totals_cents?.liability) || 0) + (Number(data.totals_cents?.equity) || 0);
    return {
      columns,
      sections,
      footer: {
        label: 'Liabilities + equity',
        values: { balance_cents: money(liabilitiesAndEquity, currency) },
      },
    };
  }

  if (report === 'ar-aging' || report === 'ap-aging') {
    const isAr = report === 'ar-aging';
    const source = isAr ? data.invoices : data.bills;
    const amountKey = isAr ? 'total_cents' : 'open_cents';
    const rows = (source || []).map((r) => ({
      number: isAr ? r.number : r.bill_number,
      party: isAr ? r.client_name : r.vendor_name,
      [amountKey]: r[amountKey] || 0,
      days_outstanding: r.days_outstanding,
      bucket: BUCKET_LABELS[r.bucket] || r.bucket,
      currency: r.currency,
    }));
    return {
      columns: [
        text('number', isAr ? 'Invoice' : 'Bill'),
        text('party', isAr ? 'Customer' : 'Vendor'),
        amount(amountKey, isAr ? 'Total' : 'Open'),
        count('days_outstanding', 'Days'),
        text('bucket', 'Bucket'),
      ],
      sections: [{ rows }],
      footer: {
        label: 'Total outstanding',
        values: { [amountKey]: money(sumBy(rows, amountKey), currency) },
      },
      buckets: Object.entries(data.buckets_cents || {}).map(([key, cents]) => ({
        key,
        label: BUCKET_LABELS[key] || key,
        cents,
        tone: BUCKET_TONES[key],
      })),
      asOf: data.as_of,
    };
  }

  return null;
}

const ReportViewer = ({ report, data, from, to, onChangeDates, onRefresh, loading, error, currency = 'USD' }) => {
  const uid = useId();
  const model = buildReport(data, report, currency);
  const sections = model?.sections || [];
  const columns = model?.columns || [];
  const grouped = sections.some((s) => s.label);
  const flatRows = sections.flatMap((section) =>
    section.rows.map((row) => (section.label ? { ...row, __section: section.label } : row))
  );
  const title = REPORT_TITLES[report] || 'Report';

  const rangeLabel = model?.asOf
    ? `As of ${model.asOf}`
    : from && to
      ? `${from} → ${to}`
      : from
        ? `From ${from}`
        : to
          ? `Through ${to}`
          : 'All posted entries';

  const exportCsv = () => {
    if (!flatRows.length) return;
    const csvColumns = grouped
      ? [{ key: '__section', label: 'Section' }, ...columns]
      : columns;
    downloadCsv(flatRows, csvColumns, `${report}.csv`);
  };

  const colSpanBefore = Math.max(1, columns.findIndex((c) => c.numeric));

  return (
    <div className="report-viewer">
      <div className="erp-report-toolbar">
        <div className="erp-report-filters">
          <div className="erp-field">
            <label htmlFor={`${uid}-from`}>From</label>
            <input
              id={`${uid}-from`}
              type="date"
              value={from || ''}
              onChange={(e) => onChangeDates?.({ from: e.target.value, to })}
            />
          </div>
          <div className="erp-field">
            <label htmlFor={`${uid}-to`}>{report?.includes('aging') ? 'As of' : 'To'}</label>
            <input
              id={`${uid}-to`}
              type="date"
              value={to || ''}
              onChange={(e) => onChangeDates?.({ from, to: e.target.value })}
            />
          </div>
        </div>
        <div className="erp-actions">
          <button type="button" className="btn btn-secondary" onClick={onRefresh} disabled={loading}>
            {loading ? <ButtonSpinner /> : <FiRefreshCw aria-hidden />}
            {loading ? 'Loading…' : 'Refresh'}
          </button>
          <button type="button" className="btn btn-primary" onClick={exportCsv} disabled={!flatRows.length}>
            <FiDownload aria-hidden /> Export CSV
          </button>
        </div>
      </div>

      <section className="erp-panel">
        <div className="erp-report-head">
          <h3 className="erp-report-title">{title}</h3>
          <p className="erp-report-range">{rangeLabel}</p>
        </div>

        {model?.buckets?.length ? (
          <div className="erp-bucket-grid">
            {model.buckets.map((bucket) => (
              <div key={bucket.key} className={`erp-bucket${bucket.tone ? ` erp-bucket--${bucket.tone}` : ''}`}>
                <span className="erp-bucket-label">{bucket.label}</span>
                <span className="erp-bucket-value">{money(bucket.cents, currency)}</span>
              </div>
            ))}
          </div>
        ) : null}

        {error ? (
          <ErpError>{errorMessage(error, 'Failed to load this report.')}</ErpError>
        ) : loading && !flatRows.length ? (
          <ErpSkeleton rows={5} label={`Loading ${title}`} />
        ) : !flatRows.length ? (
          <ErpEmpty
            icon={FiBarChart2}
            title="Nothing to report for this period"
            hint={EMPTY_HINTS[report] || 'Adjust the date range and refresh.'}
          />
        ) : (
          <div className="erp-table-scroll">
            <table className="erp-table">
              <thead>
                <tr>
                  {columns.map((c) => (
                    <th key={c.key} scope="col" className={c.numeric ? 'erp-num' : undefined}>
                      {c.label}
                    </th>
                  ))}
                </tr>
              </thead>
              {sections.map((section, si) => (
                <tbody key={section.label || si}>
                  {section.label && (
                    <tr className="erp-report-section">
                      <td colSpan={columns.length}>{section.label}</td>
                    </tr>
                  )}
                  {section.rows.length === 0 && section.label && (
                    <tr>
                      <td colSpan={columns.length} className="erp-muted">
                        No {section.label.toLowerCase()} activity in this period.
                      </td>
                    </tr>
                  )}
                  {section.rows.map((row, ri) => (
                    <tr key={`${section.label || 'rows'}-${ri}`}>
                      {columns.map((c) => (
                        <td key={c.key} className={c.numeric ? 'erp-num' : undefined}>
                          {c.render ? c.render(row) : (row[c.key] ?? '—')}
                        </td>
                      ))}
                    </tr>
                  ))}
                  {section.subtotal && (
                    <tr className="erp-report-subtotal">
                      <td colSpan={colSpanBefore}>{section.subtotal.label}</td>
                      {columns.slice(colSpanBefore).map((c) => (
                        <td key={c.key} className={c.numeric ? 'erp-num' : undefined}>
                          {section.subtotal.values[c.key] ?? ''}
                        </td>
                      ))}
                    </tr>
                  )}
                </tbody>
              ))}
              {model.footer && (
                <tfoot>
                  <tr>
                    <td colSpan={colSpanBefore}>{model.footer.label}</td>
                    {columns.slice(colSpanBefore).map((c) => (
                      <td key={c.key} className={c.numeric ? 'erp-num' : undefined}>
                        {model.footer.values[c.key] ?? ''}
                      </td>
                    ))}
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}
      </section>
    </div>
  );
};

export default ReportViewer;
